import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, CalendarDays, TrendingUp, AlertCircle, Clock,
  Building2, Plus, X, ChevronRight,
} from 'lucide-react';
import { getCustomerDashboard, getFacilities } from '../../../../services/staffingService';
import { useUser } from '../../../../context/UserContext';
import NewFacilitySlideOver from '../facilities/NewFacilitySlideOver';
import NewWorkOrderSlideOver from '../jobs/NewWorkOrderSlideOver';
import HowItWorks from './HowItWorks';

// const ONBOARDING_KEY = 'officer_customer_onboarding_dismissed';

/* ── helpers ──────────────────────────────────────────────────────── */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}
function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtRelative(d) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return fmtDate(d);
}

/* ── KPI card — compact ──────────────────────────────────────────── */
const KPICard = ({ icon: Icon, label, value, sub, accent = 'text-brand-pink', accentBg = 'bg-brand-pink/10' }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-lg border border-border bg-card px-4 py-3"
  >
    <div className="flex items-center justify-between gap-2">
      <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${accentBg}`}>
        <Icon size={14} className={accent} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="text-lg font-bold leading-tight text-card-foreground">{value}</p>
        {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
      </div>
    </div>
  </motion.div>
);

/* ── Facility card ────────────────────────────────────────────────── */
const FacilityCard = ({ facility, onClick }) => (
  <div
    onClick={onClick}
    className="group flex cursor-pointer items-center gap-3 rounded-lg border border-border bg-card p-3 transition-all hover:border-[#CC457D]/30 hover:shadow-sm"
  >
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#404880]/10 to-[#CC457D]/10">
      <Building2 size={18} className="text-[#404880]/60" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-xs font-semibold text-card-foreground">{facility.name}</p>
      {(facility.city || facility.state) && (
        <p className="text-[10px] text-muted-foreground">
          {[facility.city, facility.state].filter(Boolean).join(', ')}
        </p>
      )}
      <p className="text-[10px] font-medium text-[#CC457D]">
        {facility.activeJobCount ?? 0} active job{facility.activeJobCount !== 1 ? 's' : ''}
      </p>
    </div>
    {facility.facilityType && (
      <span className="flex-shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
        {facility.facilityType}
      </span>
    )}
    <ChevronRight size={13} className="flex-shrink-0 text-muted-foreground transition-colors group-hover:text-[#CC457D]" />
  </div>
);

/* ── shift / activity constants ──────────────────────────────────── */
const ACTIVITY_COLORS = {
  patrolling:         { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Patrol'       },
  emergency_response: { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Emergency'    },
  access_control:     { bg: 'bg-blue-100',    text: 'text-blue-700',    label: 'Access'       },
  surveillance:       { bg: 'bg-purple-100',  text: 'text-purple-700',  label: 'Surveillance' },
  inspection:         { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Inspection'   },
  break:              { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Break'        },
  administrative:     { bg: 'bg-sky-100',     text: 'text-sky-700',     label: 'Admin'        },
  other:              { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Other'        },
};

const SHIFT_STATUS_COLORS = {
  open:    { bg: 'bg-red-50',     text: 'text-red-700',     label: 'Open'    },
  filled:  { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Filled'  },
  partial: { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Partial' },
};

/* ── main component ───────────────────────────────────────────────── */
const OverviewTab = () => {
  const { customerCompanyId } = useParams();
  const history = useHistory();
  const { userInfo } = useUser();

  const [data, setData]           = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showNewFacility, setShowNewFacility] = useState(false);
  const [showNewWorkOrder, setShowNewWorkOrder] = useState(false);

  useEffect(() => {
    Promise.all([
      getCustomerDashboard(customerCompanyId),
      getFacilities(customerCompanyId),
    ])
      .then(([dash, fac]) => {
        setData(dash.data);
        setFacilities(fac.data?.facilities || fac.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customerCompanyId]);

  const handleFacilityCreated = (facility) => {
    setFacilities(prev => [facility, ...prev]);
    setShowNewFacility(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 animate-pulse rounded-lg bg-muted" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  const { kpis = {}, upcomingShifts = [], recentActivity = [] } = data || {};
  const firstName = userInfo?.firstName || 'there';
  const topFacilities = facilities.slice(0, 3);
  const unfilledCount = upcomingShifts.filter(s => (s.activeOfferCount ?? 0) < (s.staffsRequired ?? 1)).length;

  return (
    <div className="space-y-6">

      {/* Greeting */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-card-foreground">
          Good {greeting()}, {firstName} 👋
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Here's what's happening across your facilities today.
        </p>
      </motion.div>

      {/* Onboarding stepper */}
      <AnimatePresence>
        <HowItWorks
          onWorkOrder={() => setShowNewWorkOrder(true)}
          onDismiss={null}
        />
      </AnimatePresence>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KPICard
          icon={Briefcase}
          label="Active Jobs"
          value={kpis.activeJobs ?? 0}
          accent="text-[#404880]"
          accentBg="bg-[#404880]/10"
        />
        <KPICard
          icon={CalendarDays}
          label="Today's Shifts"
          value={kpis.todayShiftsCount ?? 0}
          accent="text-sky-600"
          accentBg="bg-sky-50"
        />
        <KPICard
          icon={TrendingUp}
          label="Coverage"
          value={`${kpis.coveragePct ?? 0}%`}
          sub="across all jobs"
          accent="text-emerald-600"
          accentBg="bg-emerald-50"
        />
        <KPICard
          icon={AlertCircle}
          label="Pending Approvals"
          value={kpis.pendingApprovals ?? 0}
          sub="rate approvals needed"
          accent="text-amber-600"
          accentBg="bg-amber-50"
        />
      </div>

      {/* Upcoming shifts + Recent activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Upcoming shifts — 2/3 */}
        <div className={`lg:col-span-2 overflow-hidden rounded-lg border bg-card transition-colors ${unfilledCount > 0 ? 'border-amber-400 ring-1 ring-amber-200' : 'border-border'}`}>
          <div className={`border-b px-5 py-4 transition-colors ${unfilledCount > 0 ? 'border-amber-300 bg-amber-100/60' : 'border-border'}`}>
            <div className="flex items-center gap-2">
              {unfilledCount > 0 && (
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-600 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-600" />
                </span>
              )}
              <h3 className="text-sm font-semibold text-foreground">Upcoming Shifts</h3>
            </div>
          </div>
          {upcomingShifts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CalendarDays size={28} className="mb-2 opacity-30" />
              <p className="text-sm">No upcoming shifts</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {upcomingShifts.map((s) => {
                const cfg = SHIFT_STATUS_COLORS[s.status] || SHIFT_STATUS_COLORS.open;
                return (
                  <div
                    key={s._id}
                    className="flex cursor-pointer items-center gap-4 px-5 py-3 transition-colors hover:bg-gray-100"
                    onClick={() => history.push(`/customer-company/${customerCompanyId}/jobs/${s.job_id}`)}
                  >
                    <div className="min-w-[40px] text-center">
                      <p className="text-xs font-bold text-foreground">{fmtDate(s.date).split(' ')[0]}</p>
                      <p className="text-base font-bold text-foreground">{fmtDate(s.date).split(' ')[1]}</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {s.jobLocation || 'Unknown location'}
                      </p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock size={10} /> {fmtTime(s.startTime)} – {fmtTime(s.endTime)}
                        {s.serviceType && <span className="ml-2 text-muted-foreground/60">{s.serviceType}</span>}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.text}`}>
                        {cfg.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {s.activeOfferCount ?? 0}/{s.staffsRequired}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent activity — 1/3 */}
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <div className="border-b border-border px-5 py-4">
            <h3 className="text-sm font-semibold text-foreground">Recent Activity</h3>
          </div>
          {recentActivity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Clock size={28} className="mb-2 opacity-30" />
              <p className="text-sm">No activity yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {recentActivity.map((log) => {
                const cfg = ACTIVITY_COLORS[log.activity_type] || ACTIVITY_COLORS.other;
                const name = log.user_id
                  ? `${log.user_id.firstName} ${log.user_id.lastName}`
                  : 'Officer';
                return (
                  <div key={log._id} className="px-5 py-3">
                    <div className="flex items-start gap-2.5">
                      <span className={`mt-0.5 flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.text}`}>
                        {cfg.label}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-foreground">{name}</p>
                        <p className="line-clamp-2 text-xs text-muted-foreground">{log.note}</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground/60">{fmtRelative(log.timestamp)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Facilities strip */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Your Facilities
          </h3>
          <div className="flex items-center gap-3">
            {facilities.length > 3 && (
              <button
                onClick={() => history.push(`/customer-company/${customerCompanyId}/facilities`)}
                className="text-xs text-[#CC457D] hover:underline"
              >
                View all ({facilities.length}) →
              </button>
            )}
            <button
              onClick={() => setShowNewFacility(true)}
              className="flex items-center gap-1.5 rounded-lg bg-[#CC457D] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#CC457D]/90"
            >
              <Plus size={13} /> Add facility
            </button>
          </div>
        </div>

        {topFacilities.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-10 text-muted-foreground">
            <Building2 size={24} className="mb-2 opacity-30" />
            <p className="text-sm">No facilities yet</p>
            <button
              onClick={() => setShowNewFacility(true)}
              className="mt-3 text-xs text-[#CC457D] hover:underline"
            >
              Add your first facility →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {topFacilities.map((f) => (
              <FacilityCard
                key={f._id}
                facility={f}
                onClick={() =>
                  history.push(`/customer-company/${customerCompanyId}/facilities/${f._id}`)
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* New facility slide-over */}
      <NewFacilitySlideOver
        open={showNewFacility}
        onClose={() => setShowNewFacility(false)}
        onCreated={handleFacilityCreated}
        customerCompanyId={customerCompanyId}
      />

      {/* New work order slide-over */}
      <NewWorkOrderSlideOver
        open={showNewWorkOrder}
        onClose={() => setShowNewWorkOrder(false)}
      />
    </div>
  );
};

export default OverviewTab;
